import { useState } from 'react';
import {
  verifyCurrentUserAttributeSubmit,
  updateUserAttributes,
  getCurrentAuthenticatedUser,
} from '../../../apiServices/authApi';
import { validateEmailFormat } from '../../../util/UtilFunctions';
import { updateUserEmail } from '../../../apiServices/userApi';
import { useNotification } from '@/hooks/useNotification';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Spinner } from '../../ui/Spinner';

interface UpdateEmailModalProps {
  closeModal: () => void;
  updateIsQuerying: (q: boolean) => void;
  updateCurrentUserInfo: () => void;
}

export default function UpdateEmailModal({ closeModal, updateIsQuerying, updateCurrentUserInfo }: UpdateEmailModalProps) {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const { notify } = useNotification();

  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { notify({ type: 'info', message: 'Form is not complete' }); return; }
    if (!validateEmailFormat(email)) { notify({ type: 'info', message: 'Given text is not an email address' }); setEmail(''); return; }
    setLoading(true);
    try {
      updateIsQuerying(true);
      const user = await getCurrentAuthenticatedUser();
      await updateUserAttributes(user, { email });
      updateIsQuerying(false);
      setEmailSent(true);
    } catch (error) {
      notify({ type: 'error', message: (error as Error).message });
      updateIsQuerying(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirmationCode) { notify({ type: 'error', message: 'Form not complete' }); return; }
    setLoading(true);
    try {
      updateIsQuerying(true);
      await verifyCurrentUserAttributeSubmit('email', confirmationCode);
      await updateUserEmail(email);
      updateIsQuerying(false);
      updateCurrentUserInfo();
      notify({ type: 'success', message: 'Email changed' });
      closeModal();
    } catch (error) {
      notify({ type: 'error', message: (error as Error).message });
      updateIsQuerying(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {!emailSent ? (
        <form onSubmit={handleUpdateEmail} className="space-y-4">
          <Input type="text" placeholder="New email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
          <Button type="submit" disabled={!email || loading}>
            {loading && <Spinner size="sm" />}
            Update
          </Button>
        </form>
      ) : (
        <form onSubmit={handleConfirmEmail} className="space-y-4">
          <p className="text-sm text-muted-foreground">Confirmation code is being sent to your email address</p>
          <Input type="text" placeholder="Confirmation code" value={confirmationCode} onChange={(e) => setConfirmationCode(e.target.value)} autoFocus />
          <Button type="submit" disabled={!confirmationCode || loading}>
            {loading && <Spinner size="sm" />}
            Confirm
          </Button>
        </form>
      )}
    </div>
  );
}
